/**
 * Client for the Stripe credit-pack billing routes (#125, FR-12.6).
 *
 * Two calls, mirroring `credits-api.ts`:
 *   - {@link fetchCreditPacks} — the purchasable catalog (`GET /api/billing/packs`). Public
 *     read; returns only `sku`/`credits`/`priceUsd` (the Stripe Price id stays server-side).
 *   - {@link startCheckout} — create a Checkout Session for a pack (`POST /api/billing/checkout`)
 *     and redirect the browser to the Stripe-hosted page. Identity is the signed guest token
 *     (FR-9.3); the server enforces registered-only (D-125.5) and reads the AUTHORITATIVE
 *     credit amount from its own catalog, never from the client (D-125.3).
 *
 * When Stripe is unconfigured the routes 404; the pack picker treats an empty/failed catalog
 * as "no packs" and simply doesn't render purchase buttons.
 */

import { SERVER_HTTP_URL } from '../config';
import { ApiError } from './api-error';
import { getFunnelKey } from './funnel-key';
import { recordFunnelStage } from './rum';

/** One purchasable pack as shown to the user (never carries the Stripe Price id). */
export interface CreditPack {
  /** Stable id passed back to checkout (`starter` | `popular` | `value`). */
  sku: string;
  /** Credits granted on a completed purchase. */
  credits: number;
  /** Display price in USD. */
  priceUsd: number;
}

/**
 * Fetch the purchasable pack catalog. Returns `[]` when payments are unavailable (the
 * route 404s) or on any transport error — the caller then renders no purchase buttons
 * rather than surfacing an error on a page that still works for redeem.
 */
export async function fetchCreditPacks(): Promise<CreditPack[]> {
  try {
    const res = await fetch(`${SERVER_HTTP_URL}/api/billing/packs`);
    if (!res.ok) return [];
    const body = (await res.json()) as { packs?: CreditPack[] };
    return Array.isArray(body.packs) ? body.packs : [];
  } catch {
    return [];
  }
}

/**
 * The relative path Stripe should return the buyer to after checkout — the page they
 * bought from, so a purchase off the create screen lands back on the create screen (with
 * the "Create Game" button now unblocked) rather than always the profile (PR #179 review).
 * We send only a same-origin RELATIVE path; the server prepends its own trusted base and
 * stamps `?checkout=success|cancel`, so this can never become an open redirect. Any stale
 * `checkout` param is stripped so a retry after a cancel doesn't echo it. Returns
 * `undefined` when the location is unreadable (e.g. under test), where the server falls
 * back to the profile.
 */
function currentReturnPath(): string | undefined {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('checkout');
    return `${url.pathname}${url.search}`;
  } catch {
    return undefined;
  }
}

/**
 * Start a Checkout Session for `sku` and redirect the browser to Stripe's hosted page.
 * On success this navigates away (never returns); on a rejected request throws an
 * {@link ApiError} the caller can show. A guest is rejected `403` server-side (D-125.5).
 * Sends the current page as the post-checkout return path (PR #179 review).
 */
export async function startCheckout(guestToken: string, sku: string): Promise<void> {
  // Adoption funnel (#148): the user intends to pay. Client-side stage (RUM); the server
  // ALSO emits an authoritative `checkout_started` on the session create — the client
  // event adds the pre-redirect error/session context the RUM session correlates.
  recordFunnelStage('checkout_started', { props: { sku } });
  const res = await fetch(`${SERVER_HTTP_URL}/api/billing/checkout`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      guestToken,
      sku,
      returnPath: currentReturnPath(),
      fkey: getFunnelKey(),
    }),
  });
  if (!res.ok) {
    const message =
      res.status === 403
        ? 'Sign in to buy credits.'
        : 'Could not start checkout. Please try again.';
    throw new ApiError(message, res.status);
  }
  const body = (await res.json()) as { url?: string };
  if (!body.url) {
    throw new ApiError('Could not start checkout. Please try again.', 502);
  }
  window.location.assign(body.url);
}
